// context/ReitContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  CityOption,
  LocationData,
  ReitProperty,
  SelectionRow,
} from "../types/reitProperty.type";
import service from "../services/service"; // adjust import if needed

interface ReitContextType {
  cities: CityOption[];
  setCities: React.Dispatch<React.SetStateAction<CityOption[]>>;
  selectedCity: CityOption | null;
  setSelectedCity: React.Dispatch<React.SetStateAction<CityOption | null>>;
  rows: SelectionRow[];
  setRows: React.Dispatch<React.SetStateAction<SelectionRow[]>>;
  selectedProperties: ReitProperty[];
  setSelectedProperties: React.Dispatch<React.SetStateAction<ReitProperty[]>>;
  rawData: LocationData[];
  setRawData: React.Dispatch<React.SetStateAction<LocationData[]>>;
}

const ReitContext = createContext<ReitContextType | undefined>(undefined);

export const ReitProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [cities, setCities] = useState<CityOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [rows, setRows] = useState<SelectionRow[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<ReitProperty[]>(
    []
  );
  const [rawData, setRawData] = useState<LocationData[]>([]);

  // Fetch cities on mount and set default selected property
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const data: LocationData[] = await service.fetchCities();
        setRawData(data);

        // Prepare city options for dropdown
        const cityOptions: CityOption[] = data
          .map((loc) => ({ value: loc.location, label: loc.location }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setCities(cityOptions);

        // Default selection: first city & first property
       // Default selection: first city & first property
if (data.length > 0 && data[0].cities.length > 0) {
  const firstCity = data[0].location;
  const firstProperty = data[0].cities[0];

  setSelectedCity({ value: firstCity, label: firstCity });

  setSelectedProperties([
    {
      name: firstProperty.name,
      latlng: { lat: 0, lng: 0, label: firstProperty.name }, // placeholder
      reitName: firstProperty.name,
      reitImage: "", // placeholder
    },
  ]);

  // store for initial map load
  localStorage.setItem("FIRST_SELECTED_PROPERTY", firstProperty.name);

  // Initialize rows for selector component
  setRows([
    {
      id: "1",
      city: { value: firstCity, label: firstCity },
      property: { value: firstProperty.name, label: firstProperty.name },
      propertyOptions: data[0].cities.map((p) => ({
        value: p.name,
        label: p.name,
      })),
    },
  ]);
}

      } catch (err) {
        console.error("Failed to fetch cities:", err);
      }
    };

    fetchCities();
  }, []);

  return (
    <ReitContext.Provider
      value={{
        cities,
        setCities,
        selectedCity,
        setSelectedCity,
        rows,
        setRows,
        selectedProperties,
        setSelectedProperties,
        rawData,
        setRawData,
      }}
    >
      {children}
    </ReitContext.Provider>
  );
};

export const useReitContext = (): ReitContextType => {
  const context = useContext(ReitContext);
  if (!context)
    throw new Error("useReitContext must be used within a ReitProvider");
  return context;
};
